"use client";

import * as React from "react";
import { Activity, Zap, TrendingUp, Timer, ArrowUpRight } from "lucide-react";
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart, Line, LineChart, CartesianGrid, XAxis, YAxis, Bar, BarChart, Area, AreaChart, PolarAngleAxis } from "recharts";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const lighthouseData = [{
  name: "score",
  value: 98
}];
const lighthouseConfig = {
  score: {
    label: "Accuracy",
    color: "hsl(var(--chart-3))"
  }
} satisfies ChartConfig;
const conversionData = [{
  month: "Jan",
  value: 100
}, {
  month: "Feb",
  value: 135
}, {
  month: "Mar",
  value: 160
}, {
  month: "Apr",
  value: 195
}, {
  month: "May",
  value: 240
}, {
  month: "Jun",
  value: 315
}];
const conversionConfig = {
  value: {
    label: "ROI Lift",
    color: "hsl(var(--chart-1))"
  }
} satisfies ChartConfig;
const loadTimeData = [{
  name: "Industry Avg",
  time: 120,
  fill: "hsl(var(--muted-foreground))"
}, {
  name: "Zemio AI",
  time: 450,
  fill: "hsl(var(--chart-3))"
}];
const loadTimeConfig = {
  time: {
    label: "Hours Saved",
    color: "hsl(var(--chart-3))"
  }
} satisfies ChartConfig;
export default function PerformanceMetrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="65632" id="success-metrics" ref={ref} className={`bg-background text-foreground relative overflow-hidden py-24 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--primary)_0%,_transparent_70%)] opacity-5 blur-3xl" />
      <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-[linear-gradient(to_bottom,transparent,var(--background)_90%)]" />

      <div className="container relative mx-auto px-4">
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-default font-bold tracking-tight sm:text-4xl md:text-5xl text-foreground">
            Engineered for <span className="text-primary">Intelligence</span> &{" "}
            <span className="text-accent-foreground">Growth</span>
          </h2>
          <p className="text-muted-foreground font-default mx-auto mt-4 max-w-2xl text-lg">
            We deploy high-impact AI solutions that drive measurable business results.
            Our metrics speak to the precision of our enterprise-grade logic.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          <div data-index={0} className="group">
            <Card className="bg-card border-primary/20 shadow-sm rounded-sm backdrop-blur-md transition-all duration-300 hover:border-primary/50 hover:bg-card/90 h-full flex flex-col">
              <CardHeader className="items-center pb-0 text-center">
                <CardTitle className="text-xl font-default text-foreground">Model Accuracy</CardTitle>
                <CardDescription className="font-default text-muted-foreground">Precision & Logic Training</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pb-0">
                <ChartContainer config={lighthouseConfig} className="mx-auto aspect-square max-h-[250px]">
                  <RadialBarChart data={lighthouseData} startAngle={90} endAngle={450} innerRadius={80} outerRadius={110}>
                    <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                    <RadialBar background dataKey="value" cornerRadius={30} fill="var(--color-score)" />
                    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
                      <tspan x="50%" y="50%" className="fill-accent-foreground text-4xl font-default font-bold animate-pulse">
                        98
                      </tspan>
                      <tspan x="50%" y="50%" dy="24" className="fill-muted-foreground text-sm font-default">
                        % Precision
                      </tspan>
                    </text>
                  </RadialBarChart>
                </ChartContainer>
              </CardContent>
              <CardFooter className="flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 font-default font-medium leading-none text-accent-foreground">
                  Optimized Workflows <Zap className="h-4 w-4" />
                </div>
                <div className="text-muted-foreground font-default leading-none">
                  Based on custom neural training
                </div>
              </CardFooter>
            </Card>
          </div>

          <div data-index={1} className="group">
            <Card className="bg-card border-primary/20 shadow-sm rounded-sm backdrop-blur-md transition-all duration-300 hover:border-primary/50 hover:bg-card/90 h-full flex flex-col">
              <CardHeader className="items-center pb-0 text-center">
                <CardTitle className="text-xl font-default text-foreground">Revenue Velocity</CardTitle>
                <CardDescription className="font-default text-muted-foreground">Client ROI Growth Avg</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pb-0">
                <ChartContainer config={conversionConfig} className="mx-auto aspect-square max-h-[250px] w-full">
                  <AreaChart data={conversionData}>
                    <defs>
                      <linearGradient id="fillConversion" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" strokeOpacity={0.1} />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={value => value.slice(0, 3)} stroke="hsl(var(--muted-foreground))" />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Area dataKey="value" type="natural" fill="url(#fillConversion)" stroke="var(--color-value)" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
              <CardFooter className="flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 font-default font-medium leading-none text-accent-foreground">
                  Up by 215% this year <TrendingUp className="h-4 w-4" />
                </div>
                <div className="text-muted-foreground font-default leading-none">
                  Scalable month-over-month ROI
                </div>
              </CardFooter>
            </Card>
          </div>

          <div data-index={2} className="group">
            <Card className="bg-card border-primary/20 shadow-sm rounded-sm backdrop-blur-md transition-all duration-300 hover:border-primary/50 hover:bg-card/90 h-full flex flex-col">
              <CardHeader className="items-center pb-0 text-center">
                <CardTitle className="text-xl font-default text-foreground">Time Efficiency</CardTitle>
                <CardDescription className="font-default text-muted-foreground">Process Automation</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 pb-0">
                <ChartContainer config={loadTimeConfig} className="mx-auto aspect-square max-h-[250px] w-full">
                  <BarChart data={loadTimeData} layout="vertical" margin={{
                  left: 0,
                  right: 0,
                  top: 20,
                  bottom: 20
                }}>
                    <CartesianGrid horizontal={false} strokeDasharray="3 3" strokeOpacity={0.1} />
                    <YAxis dataKey="name" type="category" tickLine={false} axisLine={false} width={100} tick={{
                    fill: "hsl(var(--foreground))",
                    fontSize: 12,
                    fontFamily: "var(--font-default)"
                  }} />
                    <XAxis type="number" hide />
                    <ChartTooltip cursor={{
                    fill: "hsl(var(--muted)/0.2)"
                  }} content={<ChartTooltipContent />} />
                    <Bar dataKey="time" layout="vertical" radius={2} barSize={32}>
                      <LabelList dataKey="time" position="right" offset={8} className="fill-accent-foreground font-default font-bold animate-pulse" fontSize={12} formatter={(val: number) => `${val}h`} />
                    </Bar>
                  </BarChart>
                </ChartContainer>
              </CardContent>
              <CardFooter className="flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 font-default font-medium leading-none text-accent-foreground">
                  450+ Hours Saved <Timer className="h-4 w-4" />
                </div>
                <div className="text-muted-foreground font-default leading-none">
                  3.7x more efficient than manual
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}
function LabelList(props: any) {
  const {
    x,
    y,
    width,
    height,
    value,
    formatter,
    className
  } = props;
  const formattedValue = formatter ? formatter(value) : value;
  return <text x={x + width + 10} y={y + height / 2} dy={4} className={className} textAnchor="start">
      {formattedValue}
    </text>;
}